<template>
  <div class="flex flex-col">
    <FlotoCrudContainer
      as-table
      prepend-new-item
      :searchable="searchable"
      :scrollable="false"
      :fetch-fn="getInvoices"
      :delete-fn="deleteInvoice"
      :create-fn="addInvoice"
      :update-fn="updateInvoice"
      :columns="tableColumns"
    >
      <template v-slot:add-controls="{ create, refreshList }">
        <MRow class="mb-4" :gutter="0">
          <MCol :size="6">
            <MInput
              v-if="searchable"
              v-model="searchTerm"
              class="search-box"
              :placeholder="$t('search')"
              @update="refreshList"
            >
              <template v-slot:prefix>
                <MIcon name="search" />
              </template>
            </MInput>
          </MCol>
          <MCol :size="6" class="text-right">
            <MButton v-if="!disabled" variant="primary" @click="create">
              {{ $t('add') }} {{ $tc('invoice') }}
            </MButton>
          </MCol>
        </MRow>
      </template>
      <template v-slot:form-header="{ item }">
        {{ $t(item.id ? 'edit' : 'add') }} {{ $tc('invoice') }}
      </template>
      <template v-slot:form-items="{ item: Invoice }">
        <InvoiceForm :form-data="Invoice" :currency-id="resource.currencyId" />
      </template>
      <template v-slot:form-actions="{ submit, cancel, item, processing }">
        <MButton :loading="processing" @click="submit">
          {{ $t(item.id ? 'update' : 'add') }}
        </MButton>
        <MButton variant="default" class="ml-2" @click="cancel">
          {{ $t('cancel') }}
        </MButton>
      </template>
      <template v-slot:invoiceNumber="{ item, edit }">
        <td class="resource-link">
          <a @click="edit">
            {{ item.invoiceNumber }}
          </a>
        </td>
      </template>
      <template v-slot:billDate="{ item }">
        <td class="text-ellipsis">
          {{ item.billDate | datetime }}
        </td>
      </template>
      <template v-slot:amount="{ item }">
        <td class="text-ellipsis">
          <div v-if="item.amount" class="flex">
            {{ (item.amount || 0).toFixed(2) }}
            <CurrencyPicker :value="resource.currencyId" text-only disabled />
          </div>
          <div v-else> --- </div>
        </td>
      </template>
      <template v-slot:dueDate="{ item }">
        <td class="text-ellipsis">
          {{ item.dueDate | datetime }}
        </td>
      </template>
      <template v-slot:description="{ item }">
        <td class="text-ellipsis">
          {{ item.description || '---' }}
        </td>
      </template>
      <template v-slot:attachment="{ item }">
        <td class="text-ellipsis">
          <FlotoAttachment :value="item.fileAttachments" only-list />
        </td>
      </template>
      <template v-slot:createdBy="{ item }">
        <td class="text-ellipsis">
          <FlotoTechnicianPicker :value="item.createdBy" disabled />
        </td>
      </template>
      <template v-slot:actions="{ item, remove, edit }">
        <td class="text-ellipsis">
          <MIcon
            name="pencil"
            class="mr-2 cursor-pointer text-primary"
            @click="edit"
          />
          <FlotoDeleteBtn
            :class="{ invisible: !item.canDelete || disabled }"
            :message="$t('confirm_delete_item', { item: $tc('invoice') })"
            @confirm="remove"
          />
        </td>
      </template>
    </FlotoCrudContainer>
  </div>
</template>

<script>
import CurrencyPicker from '@components/data-picker/currency-picker'
import { OrganizationComputed } from '@state/modules/organization'
import InvoiceForm from './invoice-form'
import {
  getInvoicesApi,
  deleteInvoiceApi,
  updateInvoiceApi,
  createInvoiceApi,
} from '../../invoice-api'
export default {
  name: 'InvoiceList',
  components: { InvoiceForm, CurrencyPicker },
  props: {
    disabled: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    resourceId: { type: Number, required: true },
    resource: {
      type: Object,
      default() {
        return {}
      },
    },
    hasActions: { type: Boolean, default: false },
  },
  data() {
    return {
      searchTerm: '',
      tableColumns: [
        {
          name: `${this.$tc('invoice')} ${this.$tc('number')}`,
          key: 'invoiceNumber',
        },
        {
          name: `${this.$tc('bill')} ${this.$tc('date')}`,
          key: 'billDate',
        },
        {
          name: `${this.$tc('invoice')} ${this.$tc('amount')}`,
          key: 'amount',
        },
        {
          name: `${this.$tc('due_date')}`,
          key: 'dueDate',
        },
        {
          name: `${this.$tc('comment', 2)}`,
          key: 'description',
        },
        {
          name: `${this.$tc('attachment', 2)}`,
          key: 'attachment',
        },
        {
          name: `${this.$tc('added_by')}`,
          key: 'createdBy',
        },
        ...(this.hasActions
          ? [
              {
                name: `${this.$tc('action', 2)}`,
                key: 'actions',
                width: '120px',
              },
            ]
          : []),
      ],
    }
  },
  computed: {
    ...OrganizationComputed,
  },
  methods: {
    getInvoices(limit, offset) {
      return getInvoicesApi(
        this.resourceId,
        { name: this.searchTerm },
        limit,
        offset
      )
    },
    deleteInvoice({ id }) {
      return deleteInvoiceApi(id).then((response) => {
        this.$emit('refresh')
        return response
      })
    },
    updateInvoice(data) {
      return updateInvoiceApi(data).then((response) => {
        this.$emit('refresh')
        return response
      })
    },
    addInvoice(data) {
      const requestData = { ...data, enabled: true }
      return createInvoiceApi(this.resourceId, requestData).then((response) => {
        this.$emit('refresh')
        return response
      })
    },
  },
}
</script>
